import React, { useEffect, useMemo, useState } from "react";
import {
  Grid,
  TextField,
  Card,
  Typography,
  FormHelperText,
  Box,
  Stack,
  InputAdornment,
  IconButton,
  Button,
} from "@mui/material";
import MDButton from "components/MDButton";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { openSnackbar } from "../../redux/slices/common";
import { AccountCircle } from "@mui/icons-material";
import CommonQuillEditor from "common/editor";
import AngleRightIcon from "common/customIcons/angle_right";
import {
  closeCustomModalDialog,
  openCustomModalDialog,
  setModalLoader,
} from "../../redux/slices/modalSlice";
import { addMainTrait, deleteMainTrait, updateMainTrait } from "../../redux/slices/traitSlice";
import DeleteIcon from "common/customIcons/delete";
import { useSearchParams } from "react-router-dom";
import Loader from "common/Loader";
import { productTraitValidation } from "validations/productTraits";
import { getPermissionsByMode } from "utilities/common";
import { hasSectionPermission } from "utilities/common";
import { getData } from "utilities/common";

const AddTraitForm = () => {
  const { user } = useSelector((state) => state.auth);
  const { traitDetails: trait = {} } = useSelector((state) => state?.traitSlice || {});
  const { loading } = useSelector((state) => state.traitSlice);
  const { traitSlug = "" } = useParams();
  const [linkState, setLinkState] = useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [errors, setErrors] = useState({});
  const [isProductSlug, setIsProductSlugData] = useState(false);

  const fullRoleTab = useMemo(() => {
    return searchParams.get("roleTab") || "hemp"; // Default to 'hemp'
  }, [searchParams]);

  const [formData, setFormData] = useState({
    data: {
      hemp: {
        status: "Active",
        createdBy: user?.name || "",
        metaTitle: "",
        metaDescription: "",
        description1: "",
        mainTraitName: "",
        desktopImages: [],
        mobileImages: [],
        traitSlug: "",
        traitHeading: "",
      },
      hyperwolf: {
        status: "Active",
        createdBy: user?.name || "",
        metaTitle: "",
        metaDescription: "",
        description1: "",
        mainTraitName: "",
        desktopImages: [],
        mobileImages: [],
        traitSlug: "",
        traitHeading: "",
      },
      stilo: {
        status: "Active",
        createdBy: user?.name || "",
        metaTitle: "",
        metaDescription: "",
        description1: "",
        mainTraitName: "",
        desktopImages: [],
        mobileImages: [],
        traitSlug: "",
        traitHeading: "",
      },
    },
  });

  let localUser = getData("login-user-info");
  if (localUser) {
    localUser = JSON.parse(localUser);
  }

  const adminMode = localStorage.getItem("adminsMode")
    ? JSON.parse(localStorage.getItem("adminsMode"))
    : null;
  const permissionsForMode = getPermissionsByMode(localUser?.permissions, adminMode);

  const canCreate = hasSectionPermission(permissionsForMode, "Manage Website", "Create");
  const canEdit = hasSectionPermission(permissionsForMode, "Manage Website", "Edit/Update");
  const canView = hasSectionPermission(permissionsForMode, "Manage Website", "View Only");
  const canDelete = hasSectionPermission(permissionsForMode, "Manage Website", "Delete");

  useEffect(() => {
    if (user?.name) {
      setFormData((prev) => ({
        ...prev,
        data: {
          hemp: { ...prev.data.hemp, createdBy: user.name },
          hyperwolf: { ...prev.data.hyperwolf, createdBy: user.name },
          stilo: { ...prev.data.stilo, createdBy: user.name },
        },
      }));
    }
  }, [user?.name]);

  // Handle input changes for the selected platform
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "traitSlug") {
      setIsProductSlugData(true);
    }

    // Update the mainTraitName for all platforms to keep it consistent
    setFormData((prevData) => {
      const updatedData = { ...prevData.data };

      // Update only the selected platform's mainTraitName
      updatedData[fullRoleTab] = {
        ...updatedData[fullRoleTab],
        [name]: value,
      };

      // Automatically update mainTraitName for all platforms when one is changed
      if (name === "mainTraitName") {
        Object.keys(updatedData).forEach((platform) => {
          if (platform !== fullRoleTab) {
            updatedData[platform] = {
              ...updatedData[platform],
              mainTraitName: value, // Ensure all platforms reflect the same name
            };
          }
        });
      }

      return { ...prevData, data: updatedData };
    });
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    const { errors, isValid } = productTraitValidation(formData, fullRoleTab);
    if (!isValid) {
      setErrors(errors);
      return;
    }
    setErrors({});

    const payload = {
      data: {
        [fullRoleTab]: formData.data[fullRoleTab],
      },
    };

    if (traitSlug) {
      const updatedPayload = { ...payload };

      // Remove traitSlug and canonical temporarily for the dispatch
      updatedPayload.data[fullRoleTab] = {
        ...updatedPayload.data[fullRoleTab],
        updatedBy: user?.name || "",
      };
      if (!isProductSlug) {
        delete updatedPayload.data[fullRoleTab].traitSlug;
      }
      delete updatedPayload.data[fullRoleTab].traitSlugArray;
      delete updatedPayload.data[fullRoleTab].canonical;
      // Update existing trait
      dispatch(updateMainTrait({ data: updatedPayload, slug: traitSlug, userType: "admin" }))
        .unwrap()
        .then((res) => {
          showResponseMessage(res?.data?.message, "success");
        })
        .catch((err) => {
          showResponseMessage(err?.message, "error");
        });
    } else {
      // Add new trait
      dispatch(addMainTrait(payload))
        .unwrap()
        .then((res) => {
          showResponseMessage(res?.message, "success");
        })
        .catch((err) => {
          showResponseMessage(err?.message, "error");
        });
    }
  };

  // Show success/error messages
  const showResponseMessage = (message, errortype) => {
    if (errortype === "success") {
      dispatch(openSnackbar({ content: message, color: "success", icon: "check" }));
    }
    if (errortype === "error") {
      dispatch(openSnackbar({ content: message, color: "error", icon: "warning" }));
    }
  };

  useEffect(() => {
    if (traitSlug && Object.keys(trait).length > 0) {
      setFormData((prevData) => {
        // Ensure that the `mainTraitName` is copied across all platforms if it exists
        const mainTraitName =
          trait.data?.hemp?.mainTraitName ||
          trait.data?.hyperwolf?.mainTraitName ||
          trait.data?.stilo?.mainTraitName ||
          prevData.data?.hemp?.mainTraitName ||
          prevData.data?.hyperwolf?.mainTraitName ||
          prevData.data?.stilo?.mainTraitName;

        const updatedData = {
          hemp: {
            ...prevData.data.hemp,
            ...trait.data.hemp, // Populate hemp data
            mainTraitName: mainTraitName, // Set mainTraitName for hemp
          },
          hyperwolf: {
            ...prevData.data.hyperwolf,
            ...trait.data.hyperwolf, // Populate hyperwolf data
            mainTraitName: mainTraitName, // Set mainTraitName for hyperwolf
          },
          stilo: {
            ...prevData.data.stilo,
            ...trait.data.stilo, // Populate stilo data
            mainTraitName: mainTraitName, // Set mainTraitName for stilo
          },
        };

        return { data: updatedData };
      });
    }
  }, [trait]);

  // Handle platform tab change
  useEffect(() => {
    setIsProductSlugData(false);
    if (traitSlug && !searchParams.has("roleTab")) {
      const updatedParams = new URLSearchParams(searchParams);
      updatedParams.set("roleTab", fullRoleTab);
      navigate(`?${updatedParams.toString()}`, { replace: true });
    }
  }, [traitSlug, searchParams, navigate, fullRoleTab]);

  const uploadThumbnailImage = {
    borderRadius: "20px",
    width: "160px",
    height: "160px",
    objectFit: "cover",
    boxShadow: "0 0 1px rgb(0 0 0 / 20%)",
  };

  const handleSave = (data) => {
    if (fullRoleTab === "all") {
      setFormData((prevData) => {
        const updatedData = { ...prevData.data };
        Object.keys(updatedData).forEach((platform) => {
          if (data.imageType === "desktop") {
            updatedData[platform] = {
              ...updatedData[platform],
              desktopImages: [...updatedData[platform].desktopImages, data.desktopImages],
            };
          } else if (data.imageType === "mobile") {
            updatedData[platform] = {
              ...updatedData[platform],
              mobileImages: [...updatedData[platform].mobileImages, data.mobileImages],
            };
          }
        });
        return {
          ...prevData,
          data: updatedData,
        };
      });
    } else {
      if (data.imageType === "desktop") {
        setFormData((prevData) => ({
          ...prevData,
          data: {
            ...prevData.data,
            [fullRoleTab]: {
              ...prevData.data[fullRoleTab],
              desktopImages: [...prevData.data[fullRoleTab].desktopImages, data.desktopImages],
            },
          },
        }));
      } else if (data.imageType === "mobile") {
        setFormData((prevData) => ({
          ...prevData,
          data: {
            ...prevData.data,
            [fullRoleTab]: {
              ...prevData.data[fullRoleTab],
              mobileImages: [...prevData.data[fullRoleTab].mobileImages, data.mobileImages],
            },
          },
        }));
      }
    }
  };

  const handleViewModal = async (index, type) => {
    const viewData = formData?.data[fullRoleTab]?.[type][index];
    dispatch(
      openCustomModalDialog({
        modalData: { type: type, handleSave, viewData: viewData },
        modal: "ADD_BANNER_MODAL",
      })
    );
  };

  const handleOpenModal = async (type) => {
    dispatch(
      openCustomModalDialog({ modalData: { type: type, handleSave }, modal: "ADD_BANNER_MODAL" })
    );
  };

  const handleDelete = (id, type) => {
    setFormData((prevData) => ({
      ...prevData,
      data: {
        ...prevData.data,
        [fullRoleTab]: {
          ...prevData.data[fullRoleTab],
          [type === "desktop" ? "desktopImages" : "mobileImages"]: prevData.data[fullRoleTab][
            type === "desktop" ? "desktopImages" : "mobileImages"
          ].filter((item) => item._id !== id),
        },
      },
    }));
  };

  useEffect(() => {
    setTimeout(() => {
      setLinkState("");
    }, 1000);
  }, [linkState]);

  const showError = (message, errortype) => {
    if (errortype === "success") {
      dispatch(openSnackbar({ content: message, color: "success", icon: "check" }));
    }
    if (errortype === "error") {
      dispatch(openSnackbar({ content: message, color: "error", icon: "warning" }));
    }
  };

  const deleteHandler = (deleteId) => {
    dispatch(setModalLoader(true));
    dispatch(deleteMainTrait({ slug: deleteId, platform: fullRoleTab }))
      .unwrap()
      .then((res) => {
        showError(res?.data?.message, "success");
        dispatch(closeCustomModalDialog());
        navigate("/product_traits");
      })
      .catch((err) => {
        dispatch(setModalLoader(false));
        showError(err?.message, "error");
      });
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <Box p={4}>
          {/********************** Top Back Section **********************/}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
            gap={2}
            mb={2}
          >
            <Box display="flex" alignItems="center" color="text.main" lineHeight={1}>
              <IconButton onClick={() => navigate("/product_traits")}>
                <AngleRightIcon fontSize="14px" />
              </IconButton>
              <Typography variant="h6" fontWeight="semibold" ml={0.75}>
                {traitSlug ? "Edit" : "Add"} Major Trait
              </Typography>
            </Box>

            <Stack
              direction="row"
              alignItems="center"
              gap={0.75}
              lineHeight={1}
              p={0.75}
              borderRadius={2}
              bgcolor="#F2F3F7"
            >
              {/* Active Button */}
              <MDButton
                sx={{
                  minWidth: "92px",
                  borderRadius: 1.75,
                  minHeight: "28px",
                  p: 0.25,
                  fontWeight: 500,
                }}
                type="button"
                variant={
                  formData.data[fullRoleTab]?.status === "Active" || fullRoleTab === "all"
                    ? "contained"
                    : "text"
                }
                color={
                  formData.data[fullRoleTab]?.status === "Active" || fullRoleTab === "all"
                    ? "success"
                    : "dark"
                }
                size="small"
                onClick={() => {
                  if (fullRoleTab === "all") {
                    // Set all platforms' status to "Active"
                    setFormData((prevData) => {
                      const updatedData = { ...prevData.data };
                      Object.keys(updatedData).forEach((platform) => {
                        updatedData[platform] = {
                          ...updatedData[platform],
                          status: "Active",
                        };
                      });
                      return {
                        ...prevData,
                        data: updatedData,
                      };
                    });
                  } else {
                    // Set the specific platform's status to "Active"
                    setFormData((prevData) => ({
                      ...prevData,
                      data: {
                        ...prevData.data,
                        [fullRoleTab]: {
                          ...prevData.data[fullRoleTab],
                          status: "Active",
                        },
                      },
                    }));
                  }
                }}
              >
                Active
              </MDButton>

              {/* Inactive Button */}
              <MDButton
                sx={{
                  minWidth: "92px",
                  borderRadius: 1.75,
                  minHeight: "28px",
                  p: 0.25,
                  fontWeight: 500,
                }}
                type="button"
                variant={
                  formData.data[fullRoleTab]?.status !== "Active" && fullRoleTab !== "all"
                    ? "contained"
                    : "text"
                }
                color={
                  formData.data[fullRoleTab]?.status !== "Active" && fullRoleTab !== "all"
                    ? "error"
                    : "dark"
                }
                size="small"
                onClick={() => {
                  if (fullRoleTab === "all") {
                    // Set all platforms' status to "Inactive"
                    setFormData((prevData) => {
                      const updatedData = { ...prevData.data };
                      Object.keys(updatedData).forEach((platform) => {
                        updatedData[platform] = {
                          ...updatedData[platform],
                          status: "Inactive",
                        };
                      });
                      return {
                        ...prevData,
                        data: updatedData,
                      };
                    });
                  } else {
                    // Set the specific platform's status to "Inactive"
                    setFormData((prevData) => ({
                      ...prevData,
                      data: {
                        ...prevData.data,
                        [fullRoleTab]: {
                          ...prevData.data[fullRoleTab],
                          status: "Inactive",
                        },
                      },
                    }));
                  }
                }}
              >
                Inactive
              </MDButton>
            </Stack>
          </Stack>

          <Stack direction="row" justifyContent="space-between" gap={1} className="mb-0">
            <Box
              className="w-100 d-flex jc-s-btwn gap-2 cannabinoids-tabs"
              style={{ marginBottom: "20px" }}
            >
              <Stack direction="row" gap={1} sx={{ background: "#ffffff" }} className="mb-0">
                <Button
                  className={fullRoleTab === "hemp" ? "active" : ""}
                  onClick={() => {
                    setSearchParams({ roleTab: "hemp" });
                    // Update only the data for the "hemp" platform
                    setFormData((prevData) => ({
                      ...prevData,
                      data: {
                        // Ensure only `data` is updated
                        ...prevData.data,
                        hemp: { ...prevData.data.hemp }, // Copy `hemp` platform data only
                      },
                    }));
                  }}
                  variant="outlined"
                  sx={{ color: "#2E2E3A", minWidth: "160px" }}
                >
                  Hemp
                </Button>

                <Button
                  onClick={() => {
                    setSearchParams({ roleTab: "hyperwolf" });
                    // Update only the data for the "hyperwolf" platform
                    setFormData((prevData) => ({
                      ...prevData,
                      data: {
                        // Ensure only `data` is updated
                        ...prevData.data,
                        hyperwolf: { ...prevData.data.hyperwolf }, // Copy `hyperwolf` platform data only
                      },
                    }));
                  }}
                  variant="outlined"
                  sx={{ color: "#2E2E3A", minWidth: "160px" }}
                  className={fullRoleTab === "hyperwolf" ? "active" : ""}
                >
                  Hyperwolf
                </Button>

                <Button
                  className={fullRoleTab === "stilo" ? "active" : ""}
                  onClick={() => {
                    setSearchParams({ roleTab: "stilo" });
                    // Update only the data for the "stilo" platform
                    setFormData((prevData) => ({
                      ...prevData,
                      data: {
                        // Ensure only `data` is updated
                        ...prevData.data,
                        stilo: { ...prevData.data.stilo }, // Copy `stilo` platform data only
                      },
                    }));
                  }}
                  variant="outlined"
                  sx={{ color: "#2E2E3A", minWidth: "160px" }}
                  // disabled
                >
                  Stilo
                </Button>
              </Stack>
              {traitSlug && formData?.data?.[fullRoleTab]?.canonical && (
                <Box
                  display="flex"
                  sx={{ cursor: "pointer" }}
                  alignItems="center"
                  color="text.main"
                  lineHeight={1}
                  gap={2}
                  style={{ position: "relative" }}
                >
                  <svg
                    style={{ cursor: "pointer" }}
                    xmlns="http://www.w3.org/2000/svg"
                    width="21"
                    height="21"
                    viewBox="0 0 21 21"
                    fill="none"
                    onClick={() => {
                      const link = formData.data?.[fullRoleTab].canonical;
                      navigator.clipboard
                        .writeText(link)
                        .then(() => setLinkState("Link copied!"))
                        .catch((err) => {});
                    }}
                  >
                    <g clipPath="url(#clip0_14557_35992)">
                      <path
                        d="M8.61203 16.7513C6.65952 16.7513 5.07031 15.162 5.07031 13.2095V4.66797H3.61203C2.34784 4.66797 1.32031 5.69534 1.32031 6.95953V18.2095C1.32031 19.4737 2.34784 20.5013 3.61203 20.5013H14.0286C15.2928 20.5013 16.3203 19.4737 16.3203 18.2095V16.7513H8.61203Z"
                        fill="#0163FF"
                      />
                      <path
                        d="M19.6536 2.79172C19.6536 1.52585 18.6277 0.5 17.362 0.5H8.61203C7.34616 0.5 6.32031 1.52585 6.32031 2.79172V13.2083C6.32031 14.4742 7.34616 15.5 8.61203 15.5H17.362C18.6277 15.5 19.6536 14.4742 19.6536 13.2083V2.79172Z"
                        fill="#0163FF"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_14557_35992">
                        <rect width="20" height="20" fill="white" transform="translate(0.5 0.5)" />
                      </clipPath>
                    </defs>
                  </svg>
                  {linkState && <p className="link-status">{linkState}</p>}
                  <Typography
                    variant="body"
                    component="a"
                    href={
                      formData.data?.[fullRoleTab].canonical
                        ? formData.data?.[fullRoleTab].canonical
                        : ""
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ textDecoration: "none", color: "inherit" }}
                    style={{
                      borderRadius: "26px",
                      color: "#0163FF",
                      fontSize: "14px",
                      fontWeight: "400",
                      border: "1px solid #0163FF",
                      padding: "10px 16px",
                    }}
                  >
                    {formData.data?.[fullRoleTab].canonical
                      ? formData.data?.[fullRoleTab].canonical
                      : ""}
                  </Typography>
                </Box>
              )}
            </Box>
          </Stack>

          {/********************** Page Details **********************/}
          <Card sx={{ p: { xs: 2, md: 3 }, mb: 2, borderRadius: 2.5, boxShadow: "none" }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h5" fontWeight="semibold" mb={2}>
                  Page Details
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Trait Name*"
                      fullWidth
                      variant="outlined"
                      name="mainTraitName"
                      className="form-input created-by p-l-field bg-transparent"
                      disabled={traitSlug ? true : false}
                      value={
                        formData?.data?.[fullRoleTab]?.mainTraitName ||
                        Object.values(formData?.data || {}).find(
                          (platform) => platform.mainTraitName
                        )?.mainTraitName ||
                        ""
                      }
                      onChange={handleInputChange}
                      error={Boolean(errors?.[fullRoleTab]?.mainTraitName)} // Check if there's an error for this platform
                      helperText={errors?.[fullRoleTab]?.mainTraitName}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      className="form-input created-by"
                      label="Created By"
                      fullWidth
                      variant="outlined"
                      name="createdBy"
                      disabled
                      value={
                        fullRoleTab === "all"
                          ? formData.data?.["hemp"]?.createdBy || "" // Show 'createdBy' from 'hemp' when 'all' is selected
                          : formData.data?.[fullRoleTab]?.createdBy || "" // Otherwise, show 'createdBy' for the specific platform
                      }
                      onChange={handleInputChange}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AccountCircle fontSize="medium" color="secondary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  {trait?.data?.[fullRoleTab] && traitSlug && (
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Trait Slug"
                        className="form-input"
                        fullWidth
                        placeholder="Trait Slug"
                        name="traitSlug"
                        value={
                          fullRoleTab === "all"
                            ? // Get the value from the first platform or default to empty
                              formData?.data?.[Object.keys(formData.data)[0]]?.traitSlug || ""
                            : formData.data?.[fullRoleTab]?.traitSlug || ""
                        }
                        onChange={handleInputChange}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Trait Heading*"
                      fullWidth
                      variant="outlined"
                      name="traitHeading"
                      value={formData?.data?.[fullRoleTab]?.traitHeading}
                      onChange={handleInputChange}
                      error={Boolean(errors?.[fullRoleTab]?.traitHeading)} // Check if there's an error for this platform
                      helperText={errors?.[fullRoleTab]?.traitHeading}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Card>

          {/********************** Meta Tags **********************/}
          <Card sx={{ p: { xs: 2, md: 3 }, mb: 2, borderRadius: 2.5, boxShadow: "none" }}>
            <Typography variant="h5" fontWeight="semibold" mb={2}>
              Meta Properties
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={12}>
                {/* <Typography variant='body2' fontWeight='medium' color="#2E2E3A" mb={1}>Meta Title</Typography> */}
                <TextField
                  label="Meta Title"
                  className="form-input"
                  fullWidth
                  placeholder="Meta Title"
                  name="metaTitle"
                  value={
                    fullRoleTab === "all"
                      ? // Get the value from the first platform or default to empty
                        formData?.data?.[Object.keys(formData.data)[0]]?.metaTitle || ""
                      : formData.data?.[fullRoleTab]?.metaTitle || ""
                  }
                  onChange={handleInputChange}
                />
                <span className="drag_drop">
                  Character Count:{" "}
                  {fullRoleTab === "all"
                    ? formData?.data?.["hemp"]?.metaTitle?.length || 0
                    : formData?.data?.[fullRoleTab]?.metaTitle?.length || 0}{" "}
                  <span>(Recommended Characters: 60)</span>
                </span>
              </Grid>

              <Grid item xs={12} md={12}>
                {/* <Typography variant='body2' fontWeight='medium' color="#2E2E3A" mb={1}>Meta Description</Typography> */}
                <TextField
                  label="Meta Description"
                  className="form-input"
                  fullWidth
                  placeholder="Meta Description"
                  name="metaDescription"
                  value={
                    fullRoleTab === "all"
                      ? // Get the value from the first platform or default to empty
                        formData?.data?.[Object.keys(formData.data)[0]]?.metaDescription || ""
                      : formData?.data?.[fullRoleTab]?.metaDescription || ""
                  }
                  onChange={handleInputChange}
                />
                <span className="drag_drop">
                  Character Count: {formData.data?.[fullRoleTab]?.metaDescription?.length || 0}{" "}
                  <span>(Recommended Characters: 160)</span>
                </span>
              </Grid>
            </Grid>
          </Card>

          {/********************** Contents Section **********************/}
          <Card sx={{ p: { xs: 2, md: 3 }, mb: 2, borderRadius: 2.5, boxShadow: "none" }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h5" fontWeight="semibold" mb={2}>
                  Contents Section
                </Typography>

                <Box width="100%" mt={2}>
                  <Typography variant='body2' fontWeight='medium' mb={1} color="#2E2E3A">
                    Desktop Image(652x323)
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={1.25} mb={1}>
                    {formData?.data[fullRoleTab]?.desktopImages?.length > 0 && (
                      <>
                        {/* Display desktop images based on the selected platform */}
                        {formData.data[fullRoleTab]?.desktopImages?.map((item, index) => (
                          <Box
                            key={index}
                            sx={{ position: "relative" }}
                            onClick={() => handleViewModal(index, "desktopImages")}
                          >
                            {/* Ensure that item.mediaType and item.imageUrl are correctly accessed */}
                            {item?.mediaType === "image" && item?.imageUrl && (
                              <img
                                src={item?.imageUrl}
                                alt={item?.imageUrl}
                                style={uploadThumbnailImage}
                              />
                            )}
                            {item?.mediaType === "video" && item?.imageUrl && (
                              <video controls style={uploadThumbnailImage}>
                                <source src={item?.imageUrl} type="video/mp4" />
                              </video>
                            )}

                            {/* Delete Icon positioned at the top-right corner of the image or video */}
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation(), handleDelete(item?._id, "desktop");
                              }}
                              sx={{
                                position: "absolute",
                                top: "-5px",
                                right: "-5px",
                                backgroundColor: "rgba(255, 255, 255, 0.7)",
                                borderRadius: "50%",
                                padding: "4px",
                                color: "#ff0000",
                                fill: "#ff0000",
                                "&:hover": {
                                  backgroundColor: "rgba(255, 0, 0, 0.2)",
                                },
                              }}
                            >
                              <DeleteIcon fontSize="small" color="error" />
                            </IconButton>
                          </Box>
                        ))}
                      </>
                    )}

                    <label htmlFor="list-image-input">
                      <Box
                        bgcolor="#F1F1F1"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexDirection="column"
                        minWidth="160px"
                        minHeight="160px"
                        borderRadius="20px"
                        p={2}
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleOpenModal("desktop")}
                      >
                        <Typography variant="body2" fontWeight="medium" textAlign="center">
                          +<br /> {formData?.listImage ? "Update" : "Add"}
                        </Typography>
                      </Box>
                    </label>
                  </Stack>
                </Box>

                <Box width="100%" mt={3}>
                  <Typography variant='body2' fontWeight='medium' mb={1} color="#2E2E3A">
                    Mobile Image(575x300)
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={1.25} mb={1}>
                    {formData?.data?.[fullRoleTab]?.mobileImages?.length > 0 && (
                      <>
                        {/* Display mobile images based on the selected platform */}
                        {formData.data[fullRoleTab]?.mobileImages?.map((item, index) => (
                          <Box
                            key={index}
                            sx={{ position: "relative" }}
                            onClick={() => handleViewModal(index, "mobileImages")}
                          >
                            {/* Ensure that item.mediaType and item.imageUrl are correctly accessed */}
                            {item?.mediaType === "image" && item?.imageUrl && (
                              <img
                                src={item?.imageUrl}
                                alt={item?.imageUrl}
                                style={uploadThumbnailImage}
                              />
                            )}
                            {item?.mediaType === "video" && item?.imageUrl && (
                              <video controls style={uploadThumbnailImage}>
                                <source src={item?.imageUrl} type="video/mp4" />
                              </video>
                            )}

                            {/* Delete Icon positioned at the top-right corner of the image or video */}
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(item?._id, "mobile");
                              }}
                              sx={{
                                position: "absolute",
                                top: "-5px",
                                right: "-5px",
                                backgroundColor: "rgba(255, 255, 255, 0.7)",
                                borderRadius: "50%",
                                padding: "4px",
                                color: "#ff0000",
                                fill: "#ff0000",
                                "&:hover": {
                                  backgroundColor: "rgba(255, 0, 0, 0.2)",
                                },
                              }}
                            >
                              <DeleteIcon fontSize="small" color="error" />
                            </IconButton>
                          </Box>
                        ))}
                      </>
                    )}

                    <label htmlFor="list-image-input">
                      <Box
                        bgcolor="#F1F1F1"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexDirection="column"
                        minWidth="160px"
                        minHeight="160px"
                        borderRadius="20px"
                        p={2}
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleOpenModal("mobile")}
                      >
                        <Typography variant="body2" fontWeight="medium" textAlign="center">
                          +<br /> {formData?.listImage ? "Update" : "Add"}
                        </Typography>
                      </Box>
                    </label>
                  </Stack>
                  {Boolean(errors?.listImage) && (
                    <FormHelperText error>{errors?.listImage}</FormHelperText>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={2.5} mb={2}>
                  <Grid item xs={12}>
                    <Typography variant="body2" fontWeight="medium" color="#2E2E3A" mb={1}>
                      Title
                    </Typography>
                    <Box className="remove-quill-mb">
                      <CommonQuillEditor
                        value={
                          fullRoleTab === "all"
                            ? formData?.data?.[Object.keys(formData.data)[0]]?.title || ""
                            : formData?.data?.[fullRoleTab]?.title || ""
                        }
                        onChange={(value) => {
                          if (fullRoleTab === "all") {
                            // Update the title for all platforms
                            setFormData((prevData) => {
                              const updatedData = { ...prevData.data };
                              Object.keys(updatedData).forEach((platform) => {
                                updatedData[platform] = {
                                  ...updatedData[platform],
                                  title: value,
                                };
                              });
                              return {
                                ...prevData,
                                data: updatedData,
                              };
                            });
                          } else {
                            // Update the title for the selected platform
                            setFormData((prevData) => ({
                              ...prevData,
                              data: {
                                ...prevData.data,
                                [fullRoleTab]: {
                                  ...prevData.data[fullRoleTab],
                                  title: value,
                                },
                              },
                            }));
                          }
                        }}
                      />
                      {/* {
                    errors?.title && <FormHelperText error>{errors?.title}</FormHelperText>
                  } */}
                  </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="body2" fontWeight="medium" color="#2E2E3A" mb={1}>
                      Description1
                    </Typography>
                    <Box className="remove-quill-mb">
                    <CommonQuillEditor
                      style={true}
                      height={"300px"}
                      value={formData.data[fullRoleTab]?.description1 || ""} // Make sure to use the `description1` for the correct platform
                      onChange={(value) => {
                        setFormData((prevData) => ({
                          ...prevData,
                          data: {
                            ...prevData.data,
                            [fullRoleTab]: {
                              ...prevData.data[fullRoleTab],
                              description1: value, // Update the description1 for the correct platform
                            },
                          },
                        }));
                      }}
                    />
                    {errors?.description1 && (
                      <FormHelperText error>{errors?.description1}</FormHelperText>
                    )}
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="body2" fontWeight="medium" color="#2E2E3A" mb={1}>
                      Description2
                    </Typography>
                    <Box className="remove-quill-mb">
                      <CommonQuillEditor
                        style={true}
                        height={"300px"}
                        value={
                          fullRoleTab === "all"
                            ? formData.data[Object.keys(formData.data)[0]]?.description2 || ""
                            : formData.data[fullRoleTab]?.description2 || ""
                        }
                        onChange={(value) => {
                          if (fullRoleTab === "all") {
                            // Update description2 for all platforms
                            setFormData((prevData) => {
                              const updatedData = { ...prevData.data };
                              Object.keys(updatedData).forEach((platform) => {
                                updatedData[platform] = {
                                  ...updatedData[platform],
                                  description2: value,
                                };
                              });
                              return {
                                ...prevData,
                                data: updatedData,
                              };
                            });
                          } else {
                            // Update description2 for the selected platform
                            setFormData((prevData) => ({
                              ...prevData,
                              data: {
                                ...prevData.data,
                                [fullRoleTab]: {
                                  ...prevData.data[fullRoleTab],
                                  description2: value,
                                },
                              },
                            }));
                          }
                        }}
                      />
                      {errors?.description2 && (
                        <FormHelperText error>{errors?.description2}</FormHelperText>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Card>

          {/********************** Save, Cancel Buttons **********************/}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mt={2} mb={2}>
                {traitSlug ? (
                  <MDButton
                    sx={{ minWidth: "160px" }}
                    type="button"
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    loading={loading}
                    disabled={loading || !canEdit}
                  >
                    Update
                  </MDButton>
                ) : (
                  <MDButton
                    sx={{ minWidth: "160px" }}
                    type="button"
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    loading={loading}
                    disabled={loading || !canCreate}
                  >
                    Save
                  </MDButton>
                )}

                {traitSlug && (
                  <MDButton
                    disabled={!canDelete ? true : false}
                    sx={{ minWidth: "160px", backgroundColor: "#fdd5d3", color: "#FD4438" }}
                    type="button"
                    variant="contained"
                    color="error"
                    onClick={() => {
                      dispatch(
                        openCustomModalDialog({
                          modalData: { deleteHandler, deleteId: traitSlug },
                          modal: "DELETE_MODAL",
                        })
                      );
                    }}
                  >
                    Delete
                  </MDButton>
                )}

                <MDButton
                  sx={{ minWidth: "160px" }}
                  type="button"
                  variant="outlined"
                  color="dark"
                  onClick={() => navigate("/product_traits")}
                >
                  Cancel
                </MDButton>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      )}
    </>
  );
};

export default AddTraitForm;
